// Package Imports
import { queryOptions, useMutation } from '@tanstack/react-query'

// Core Imports
import type { TResponseError } from '@/core/types/axios'
import type { TMutation } from '@/core/types/react-query'

// Module Imports
import {
  activateBatch,
  deleteBatch,
  deliverBatch,
  editDraftBatch,
  getBatchDetail,
  getBatchList,
  getBatchQuestions,
  getLlmOptions,
  passiveBatch,
  reviewQuestion,
  startGeneration,
} from '@/modules/question-generation/question-generation.service'
import type {
  TEditDraftRequest,
  TGeneratedQuestion,
  TGenerationBatch,
  TReviewQuestionRequest,
  TStartGenerationRequest,
} from '@/modules/question-generation/question-generation.types'

export const llmOptionsQueryOptions = () =>
  queryOptions({
    queryKey: ['generationLlmOptions'],
    queryFn: getLlmOptions,
    staleTime: 5 * 60 * 1000, // config nadiren değişir
  })

export const batchListQueryOptions = (
  page: number,
  query: string,
  kind?: string
) =>
  queryOptions({
    queryKey: ['generationBatchList', page, query, kind ?? 'ALL'],
    queryFn: () => getBatchList(page, query, kind),
    // Poll while any batch is still in progress (üretiliyor/doğrulanıyor) so its
    // status flips to COMPLETED/FAILED without a manual refresh.
    refetchInterval: (q) =>
      q.state.data?.data.some(
        (b) => b.status === 'GENERATING' || b.status === 'VALIDATING'
      )
        ? 5000
        : false,
  })

export const batchDetailQueryOptions = (id: string) =>
  queryOptions({
    queryKey: ['generationBatchDetail', id],
    queryFn: () => getBatchDetail(id),
    staleTime: 0,
    refetchInterval: (q) =>
      q.state.data?.status === 'GENERATING' ||
      q.state.data?.status === 'VALIDATING'
        ? 5000
        : false,
  })

export const batchQuestionsQueryOptions = (id: string, poll = false) =>
  queryOptions({
    queryKey: ['generationBatchQuestions', id],
    queryFn: () => getBatchQuestions(id),
    staleTime: 0,
    refetchInterval: poll ? 5000 : false,
  })

export function useStartGeneration({
  onSuccess,
  onError,
}: TMutation<TGenerationBatch, TResponseError, TStartGenerationRequest>) {
  return useMutation<TGenerationBatch, TResponseError, TStartGenerationRequest>(
    {
      mutationFn: startGeneration,
      onSuccess,
      onError,
    }
  )
}

export function useEditDraft({
  onSuccess,
  onError,
}: TMutation<TGenerationBatch, TResponseError, TEditDraftRequest>) {
  return useMutation<TGenerationBatch, TResponseError, TEditDraftRequest>({
    mutationFn: editDraftBatch,
    onSuccess,
    onError,
  })
}

export function useReviewQuestion({
  onSuccess,
  onError,
}: TMutation<TGeneratedQuestion, TResponseError, TReviewQuestionRequest>) {
  return useMutation<
    TGeneratedQuestion,
    TResponseError,
    TReviewQuestionRequest
  >({
    mutationFn: reviewQuestion,
    onSuccess,
    onError,
  })
}

export function usePassiveBatch({
  onSuccess,
  onError,
}: TMutation<void, TResponseError, string>) {
  return useMutation<void, TResponseError, string>({
    mutationFn: passiveBatch,
    onSuccess,
    onError,
  })
}

export function useActivateBatch({
  onSuccess,
  onError,
}: TMutation<void, TResponseError, string>) {
  return useMutation<void, TResponseError, string>({
    mutationFn: activateBatch,
    onSuccess,
    onError,
  })
}

export function useDeleteBatch({
  onSuccess,
  onError,
}: TMutation<void, TResponseError, string>) {
  return useMutation<void, TResponseError, string>({
    mutationFn: deleteBatch,
    onSuccess,
    onError,
  })
}

export function useDeliverBatch({
  onSuccess,
  onError,
}: TMutation<TGenerationBatch, TResponseError, string>) {
  return useMutation<TGenerationBatch, TResponseError, string>({
    mutationFn: deliverBatch,
    onSuccess,
    onError,
  })
}
